import React, {useMemo, useState} from "react";
import {keepPreviousData, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography} from "@mui/material";
import {useNavigate, useParams} from "react-router";
import {enqueueSnackbar} from "notistack";
import {Table} from "../components/table/Table";
import {TableHeaderProps} from "../components/table/TableTypes";
import {TextInput} from "../components/form/TextInput";
import {AbstractDropDown} from "../components/form/AbstractDropDown";
import {
    createCity,
    createDistrict,
    deleteCity,
    deleteDistrict,
    discoverDistricts,
    loadManageCities,
    loadManageCity,
    loadManageCountries,
    updateCity,
    updateDistrict,
} from "../service/geo.service";
import {GeoCity, GeoDistrict} from "../types/Geo";
import {Delete} from "../components/common/Delete";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";

export const CitiesPage: React.FC = () => {
    const {id} = useParams();
    if (id && id !== "new") {
        return <CityEditPage id={id}/>;
    }
    return <CitiesListPage/>;
};

function CitiesListPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [addOpen, setAddOpen] = useState(false);
    const [countryId, setCountryId] = useState("");
    const [name, setName] = useState("");
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);

    const countriesQuery = useQuery({
        queryKey: ["geo-manage-countries"],
        queryFn: () => loadManageCountries().then((r) => r.data),
    });
    const citiesQuery = useQuery({
        queryKey: ["geo-manage-cities"],
        queryFn: () => loadManageCities().then((r) => r.data),
        placeholderData: keepPreviousData,
    });

    const createMutation = useMutation({
        mutationFn: () => createCity(countryId, name).then((r) => r.data),
        onSuccess: (city) => {
            enqueueSnackbar({message: "City added. Districts were looked up from the map.", variant: "success"});
            queryClient.invalidateQueries({queryKey: ["geo-manage-cities"]});
            queryClient.invalidateQueries({queryKey: ["geo-countries"]});
            setAddOpen(false);
            setName("");
            navigate("/cities/" + city.id);
        },
        onError: (error: {message?: string}) => {
            enqueueSnackbar({message: "Failed to add city: " + (error.message || "unknown error"), variant: "error"});
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (cityId: string) => deleteCity(cityId).then(() => void 0),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["geo-manage-cities"]});
            queryClient.invalidateQueries({queryKey: ["geo-countries"]});
        },
        onError: (error: {message?: string}) => {
            enqueueSnackbar({message: "Failed to delete city: " + (error.message || "unknown error"), variant: "error"});
        },
    });

    const rows = citiesQuery.data || [];
    const headers: TableHeaderProps<GeoCity>[] = useMemo(() => [
        {key: "countryName", title: "Country", render: (row) => row.countryName},
        {key: "name", title: "City", render: (row) => row.name},
        {key: "listingCount", title: "Listings", alignment: "right", render: (row) => row.listingCount},
        {key: "radiusKm", title: "Radius km", alignment: "right", render: (row) => row.radiusKm ?? ""},
        {
            key: "controlPanel",
            disableSort: true,
            title: <Stack flexDirection={"row-reverse"}><Button onClick={() => setAddOpen(true)}>Add city</Button></Stack>,
            render: (row) => <Stack flexDirection={"row"} gap="5px">
                <IconButton aria-label={"edit " + row.name} onClick={() => navigate("/cities/" + row.id)}><EditNoteOutlinedIcon/></IconButton>
                <Delete onConfirm={() => deleteMutation.mutate(row.id)}/>
            </Stack>,
        },
    ], [deleteMutation, navigate]);

    return (
        <>
            <Typography variant="h5" component="h1" gutterBottom>Cities</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Countries Relapka operates in, and the cities bound to them. Adding a city geocodes it and looks up districts.
            </Typography>
            {citiesQuery.error ? <Alert severity="error">Failed to load cities.</Alert> : null}
            <Table
                data={rows.slice(page * size, page * size + size)}
                headers={headers}
                total={rows.length}
                page={page}
                size={size}
                isLoading={citiesQuery.isFetching}
                pageChange={(nextPage, nextSize) => {
                    setPage(nextPage);
                    setSize(nextSize);
                }}
            />
            <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add city</DialogTitle>
                <DialogContent>
                    <Stack gap={2} sx={{mt: 1}}>
                        <AbstractDropDown
                            label="Country"
                            value={countryId}
                            onChange={(value) => setCountryId(value)}
                            options={(countriesQuery.data || []).map((country) => ({value: country.id, label: country.name}))}
                        />
                        <TextInput label="City name" value={name} onChange={(e) => setName(e.target.value)}/>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!countryId || !name.trim() || createMutation.isPending}
                        onClick={() => createMutation.mutate()}
                    >
                        {createMutation.isPending ? "Finding city…" : "Add city"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

function CityEditPage({id}: {id: string}) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const cityQuery = useQuery({
        queryKey: ["geo-manage-city", id],
        queryFn: () => loadManageCity(id).then((r) => r.data),
    });
    const city = cityQuery.data;
    const [name, setName] = useState("");
    const [radiusKm, setRadiusKm] = useState("");
    const [districtName, setDistrictName] = useState("");
    const [editing, setEditing] = useState<GeoDistrict | null>(null);

    React.useEffect(() => {
        if (city) {
            setName(city.name);
            setRadiusKm(String(city.radiusKm ?? 25));
        }
    }, [city]);

    const saveCity = useMutation({
        mutationFn: () => updateCity(id, {name, radiusKm: Number(radiusKm) || 25}).then((r) => r.data),
        onSuccess: () => {
            enqueueSnackbar({message: "City saved", variant: "success"});
            queryClient.invalidateQueries({queryKey: ["geo-manage-city", id]});
            queryClient.invalidateQueries({queryKey: ["geo-manage-cities"]});
            queryClient.invalidateQueries({queryKey: ["geo-countries"]});
        },
        onError: (error: {message?: string}) => enqueueSnackbar({message: error.message || "Save failed", variant: "error"}),
    });

    const discover = useMutation({
        mutationFn: () => discoverDistricts(id).then((r) => r.data),
        onSuccess: () => {
            enqueueSnackbar({message: "Districts refreshed from the map", variant: "success"});
            queryClient.invalidateQueries({queryKey: ["geo-manage-city", id]});
        },
        onError: (error: {message?: string}) => enqueueSnackbar({message: error.message || "Discover failed", variant: "error"}),
    });

    const saveDistrict = useMutation({
        mutationFn: () => {
            if (editing && editing.id) {
                return updateDistrict(editing.id, {name: editing.name, aliases: editing.aliases, lat: editing.lat, lng: editing.lng, radiusKm: editing.radiusKm});
            }
            return createDistrict(id, {name: districtName});
        },
        onSuccess: () => {
            setDistrictName("");
            setEditing(null);
            queryClient.invalidateQueries({queryKey: ["geo-manage-city", id]});
        },
        onError: (error: {message?: string}) => enqueueSnackbar({message: error.message || "District save failed", variant: "error"}),
    });

    const removeDistrict = useMutation({
        mutationFn: (districtId: string) => deleteDistrict(districtId).then(() => void 0),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["geo-manage-city", id]}),
    });

    const districts = city?.districts || [];
    const headers: TableHeaderProps<GeoDistrict>[] = [
        {key: "name", title: "District", render: (row) => row.name},
        {key: "radiusKm", title: "Radius km", alignment: "right", render: (row) => row.radiusKm ?? ""},
        {key: "lat", title: "Lat", alignment: "right", render: (row) => row.lat ?? ""},
        {key: "lng", title: "Lng", alignment: "right", render: (row) => row.lng ?? ""},
        {
            key: "controlPanel",
            disableSort: true,
            title: " ",
            render: (row) => <Stack flexDirection={"row"}>
                <IconButton aria-label={"edit district " + row.name} onClick={() => setEditing({...row})}><EditNoteOutlinedIcon/></IconButton>
                <Delete onConfirm={() => removeDistrict.mutate(row.id)}/>
            </Stack>,
        },
    ];

    if (cityQuery.isLoading) {
        return <Typography>Loading city…</Typography>;
    }
    if (!city) {
        return <Alert severity="error">City not found.</Alert>;
    }

    return (
        <Stack gap={2}>
            <Button onClick={() => navigate("/cities")} sx={{alignSelf: "flex-start"}}>Back to cities</Button>
            <Typography variant="h5" component="h1">Edit city</Typography>
            <Typography color="text.secondary">{city.countryName}</Typography>
            <TextInput label="City name" value={name} onChange={(e) => setName(e.target.value)}/>
            <TextInput label="Radius km" value={radiusKm} onChange={(e) => setRadiusKm(e.target.value)}/>
            <Stack direction="row" gap={1}>
                <Button variant="contained" onClick={() => saveCity.mutate()} disabled={saveCity.isPending}>Save city</Button>
                <Button onClick={() => discover.mutate()} disabled={discover.isPending}>Find districts</Button>
            </Stack>

            <Typography variant="h6" component="h2">Districts</Typography>
            <Stack direction={{xs: "column", sm: "row"}} gap={1}>
                <TextInput label="New district name" value={districtName} onChange={(e) => setDistrictName(e.target.value)}/>
                <Button disabled={!districtName.trim() || saveDistrict.isPending} onClick={() => saveDistrict.mutate()}>Add district</Button>
            </Stack>
            <Table
                data={districts}
                headers={headers}
                total={districts.length}
                page={0}
                size={Math.max(districts.length, 5)}
                isLoading={cityQuery.isFetching}
                pageChange={() => undefined}
            />

            <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
                <DialogTitle>Edit district</DialogTitle>
                <DialogContent>
                    {editing && <Stack gap={2} sx={{mt: 1}}>
                        <TextInput label="Name" value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})}/>
                        <TextInput label="Aliases (comma separated)" value={(editing.aliases || []).join(", ")} onChange={(e) => setEditing({
                            ...editing,
                            aliases: e.target.value.split(",").map((part) => part.trim()).filter(Boolean),
                        })}/>
                        <TextInput label="Latitude" value={String(editing.lat ?? "")} onChange={(e) => setEditing({...editing, lat: Number(e.target.value)})}/>
                        <TextInput label="Longitude" value={String(editing.lng ?? "")} onChange={(e) => setEditing({...editing, lng: Number(e.target.value)})}/>
                        <TextInput label="Radius km" value={String(editing.radiusKm ?? "")} onChange={(e) => setEditing({...editing, radiusKm: Number(e.target.value)})}/>
                    </Stack>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditing(null)}>Cancel</Button>
                    <Button variant="contained" onClick={() => saveDistrict.mutate()}>Save</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
